import type {
  StaffCatalogLocationAssessment,
  StaffCatalogPlaceMatchState,
  StaffCatalogPlaceRefreshState,
  StaffCatalogPlacesErrorCode,
  StaffCatalogPlaceView,
} from "@lafayette/schemas";

import { formatDate } from "../labels";
import {
  assessmentExplanations,
  assessmentLabels,
  matchStateLabels,
  placesErrorSentences,
} from "./labels";

// How a place state is shown. Every state is a glyph and a word, so it never depends on colour alone.
// This module is pure: it reads codes and flags from the place view and holds no provider content.

export type PlaceTone = "ok" | "warn" | "error" | "neutral";

export type PlaceBadgeInfo = Readonly<{
  /** Stable key for tests and CSS (`matched`, `needs_review`, `stale` ...). */
  key: string;
  label: string;
  glyph: string;
  tone: PlaceTone;
  /** One sentence that says what the state means and what to do. */
  explanation: string;
}>;

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** `Error: the place service did not answer in time`. A missing code gives the plain word. */
export function errorSentence(code: StaffCatalogPlacesErrorCode | null): string {
  return code === null ? "Error" : `Error: ${placesErrorSentences[code]}`;
}

/**
 * The first badge: where the item is in the matching flow. A place id that nobody has confirmed (it came
 * from an edit or an import) is "Matched (unverified)" whatever the stored state says.
 */
export function matchBadgeOf(
  matchState: StaffCatalogPlaceMatchState | null,
  unverified = false,
): PlaceBadgeInfo {
  if (unverified && matchState !== "NEEDS_REVIEW") {
    return {
      key: "matched_unverified",
      label: "Matched (unverified)",
      glyph: "◐",
      tone: "warn",
      explanation:
        "This item has a place id from an edit or an import. Nobody has checked it yet. Use Verify now, or find the place again.",
    };
  }
  switch (matchState) {
    case "MATCHED":
      return {
        key: "matched",
        label: matchStateLabels.MATCHED,
        glyph: "✓",
        tone: "ok",
        explanation: "A staff member matched this item to a place and the place id is checked.",
      };
    case "NEEDS_REVIEW":
      return {
        key: "needs_review",
        label: matchStateLabels.NEEDS_REVIEW,
        glyph: "⚠",
        tone: "error",
        explanation:
          "The stored place id is no longer known to the place service. Find the place again and correct the match.",
      };
    case "CANDIDATES_FOUND":
      return {
        key: "ambiguous",
        label: matchStateLabels.CANDIDATES_FOUND,
        glyph: "◇",
        tone: "warn",
        explanation: "A search found several places and nobody chose one. Choose the right one.",
      };
    case "NO_MATCH":
      return {
        key: "no_match",
        label: matchStateLabels.NO_MATCH,
        glyph: "○",
        tone: "warn",
        explanation: "The last search found no place. Search again with other words.",
      };
    case "NO_PLACE":
      return {
        key: "no_place",
        label: matchStateLabels.NO_PLACE,
        glyph: "▪",
        tone: "neutral",
        explanation: "A staff member said this item has no physical place.",
      };
    case "UNMATCHED":
      return {
        key: "unmatched",
        label: matchStateLabels.UNMATCHED,
        glyph: "○",
        tone: "neutral",
        explanation: "A staff member removed the match. The item has no place id now.",
      };
    default:
      return {
        key: "not_matched",
        label: "Not matched",
        glyph: "○",
        tone: "neutral",
        explanation: "This item is not matched to a place yet.",
      };
  }
}

/**
 * The second badge: how fresh the check of the place id is. It has meaning for an item with a place id.
 * `null` means there is nothing to say.
 */
export function refreshBadgeOf(
  refreshState: StaffCatalogPlaceRefreshState | null,
  lastErrorCode: StaffCatalogPlacesErrorCode | null,
  lastVerifiedAt: Date | null,
): PlaceBadgeInfo | null {
  switch (refreshState) {
    case "STALE":
      return {
        key: "stale",
        label: "Stale",
        glyph: "⚠",
        tone: "warn",
        explanation:
          lastErrorCode === null
            ? "The place id was not checked for a long time. The item is not changed."
            : `The last check failed (${placesErrorSentences[lastErrorCode]}). The item is not changed. The check runs again later.`,
      };
    case "ERROR":
      return {
        key: "error",
        label: errorSentence(lastErrorCode),
        glyph: "✕",
        tone: "error",
        explanation:
          "The checks failed too many times. The item is not changed. Ask a supervisor if it continues.",
      };
    case "FRESH":
      return {
        key: "fresh",
        label:
          lastVerifiedAt === null ? "Not checked yet" : `Checked ${formatDate(lastVerifiedAt)}`,
        glyph: lastVerifiedAt === null ? "○" : "✓",
        tone: lastVerifiedAt === null ? "neutral" : "ok",
        explanation:
          lastVerifiedAt === null
            ? "The place id has not been checked since it was stored."
            : "The place service still knows this place id.",
      };
    default:
      return null;
  }
}

/**
 * The check badge of a review row. The row carries no place id, so the match state stands in for it: an
 * item that is matched, or whose stored id needs review, has an id to check. A failed check always shows.
 * For any other item (ambiguous, no match, cleared) the check does not apply, and `null` says so.
 */
export function rowRefreshBadge(
  matchState: StaffCatalogPlaceMatchState | null,
  refreshState: StaffCatalogPlaceRefreshState | null,
  lastErrorCode: StaffCatalogPlacesErrorCode | null,
  lastVerifiedAt: Date | null,
): PlaceBadgeInfo | null {
  const hasId = matchState === "MATCHED" || matchState === "NEEDS_REVIEW";
  if (!hasId && refreshState !== "STALE" && refreshState !== "ERROR") return null;
  return refreshBadgeOf(refreshState, lastErrorCode, lastVerifiedAt);
}

export function viewBadges(
  view: Pick<StaffCatalogPlaceView, "state" | "current">,
): readonly PlaceBadgeInfo[] {
  const unverified = view.current !== null && !view.current.verified;
  const badges = [matchBadgeOf(view.state.matchState, unverified)];
  if (view.current !== null || view.state.matchState === "NEEDS_REVIEW") {
    const refresh = refreshBadgeOf(
      view.state.refreshState,
      view.state.lastErrorCode,
      view.state.lastVerifiedAt,
    );
    if (refresh !== null && !(unverified && refresh.key === "fresh")) badges.push(refresh);
  }
  return badges;
}

/** The badge of one location group. */
export function assessmentBadge(assessment: StaffCatalogLocationAssessment): PlaceBadgeInfo {
  const shape: Readonly<
    Record<StaffCatalogLocationAssessment, Pick<PlaceBadgeInfo, "glyph" | "tone">>
  > = {
    CONFIRMED: { glyph: "✓", tone: "ok" },
    UNCONFIRMED: { glyph: "⚠", tone: "warn" },
    UNKNOWN: { glyph: "?", tone: "neutral" },
    NOT_ASSESSED: { glyph: "○", tone: "warn" },
  };
  return {
    key: assessment.toLowerCase(),
    label: capitalize(assessmentLabels[assessment].toLowerCase()),
    explanation: assessmentExplanations[assessment],
    ...shape[assessment],
  };
}
